import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';

import ErrorMessage from '@/components/atoms/ErrorMessage';
import { Button } from '@/components/atoms/Button';
import PasswordInput from '@/components/atoms/Form/PasswordInput/PasswordInput';
import { Routes } from '@/core/router/routes.types';
import { useResetPasswordMutation, useVerifyForgotPasswordTokenQuery } from '@/services/innovorder/auth/auth.endpoints';
import { getErrorCode, stringifyError } from '@/utils/errors';
import {
    Actions,
    Card,
    InputWrapper,
    Page,
    SecondaryLink,
    Subtitle,
    SuccessMessage,
    Title,
} from '../ForgotPassword/ForgotPassword.style';

type RouteParams = { token: string; userId: string };

const MIN_PASSWORD_LENGTH = 8;

const ResetPassword: React.FunctionComponent = () => {
    const history = useHistory();
    const { token, userId: userIdParam } = useParams<RouteParams>();
    const userId = Number(userIdParam);
    const isUserIdValid = Number.isFinite(userId) && userId > 0;

    const {
        isError: tokenIsInvalid,
        isLoading: tokenValidating,
        error: tokenError,
    } = useVerifyForgotPasswordTokenQuery({ token, userId }, { skip: !token || !isUserIdValid });

    const [resetPassword, { isLoading, error, isSuccess }] = useResetPasswordMutation();

    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [localError, setLocalError] = useState<string | null>(null);

    const passwordsMatch = password && password === confirm;
    const isPasswordLongEnough = password.length >= MIN_PASSWORD_LENGTH;

    const handleSubmit = useCallback(
        async (event: React.FormEvent) => {
            event.preventDefault();
            setLocalError(null);
            if (!isPasswordLongEnough) {
                setLocalError('password_too_short');
                return;
            }
            if (!passwordsMatch) {
                setLocalError('passwords_mismatch');
                return;
            }
            try {
                await resetPassword({ token, userId, newPassword: password }).unwrap();
            } catch {
                // Render via `error` below
            }
        },
        [isPasswordLongEnough, passwordsMatch, resetPassword, token, userId, password],
    );

    if (!isUserIdValid || (tokenIsInvalid && !tokenValidating)) {
        return (
            <Page>
                <Card onSubmit={(e) => e.preventDefault()}>
                    <Title>
                        <FormattedMessage id="resetPassword.invalidLinkTitle" />
                    </Title>
                    <ErrorMessage>
                        {tokenError ? stringifyError(tokenError) : <FormattedMessage id="resetPassword.invalidLink" />}
                    </ErrorMessage>
                    <Actions>
                        <Button buttonType="primary" fullWidth thick onClick={() => history.push(Routes.Login)}>
                            <FormattedMessage id="resetPassword.backToLogin" />
                        </Button>
                    </Actions>
                </Card>
            </Page>
        );
    }

    if (isSuccess) {
        return (
            <Page>
                <Card onSubmit={(e) => e.preventDefault()}>
                    <Title>
                        <FormattedMessage id="resetPassword.successTitle" />
                    </Title>
                    <SuccessMessage>
                        <FormattedMessage id="resetPassword.successMessage" />
                    </SuccessMessage>
                    <Actions>
                        <Button buttonType="primary" fullWidth thick onClick={() => history.push(Routes.Login)}>
                            <FormattedMessage id="resetPassword.backToLogin" />
                        </Button>
                    </Actions>
                </Card>
            </Page>
        );
    }

    const errorCode = error ? getErrorCode(error) : null;
    const errorLabel =
        localError === 'password_too_short' ? (
            <FormattedMessage id="resetPassword.passwordTooShort" values={{ min: MIN_PASSWORD_LENGTH }} />
        ) : localError === 'passwords_mismatch' ? (
            <FormattedMessage id="resetPassword.passwordsMismatch" />
        ) : errorCode === 'forgottenPassword_token_invalid' ? (
            <FormattedMessage id="resetPassword.invalidLink" />
        ) : error ? (
            stringifyError(error)
        ) : null;

    return (
        <Page>
            <Card onSubmit={handleSubmit}>
                <Title>
                    <FormattedMessage id="resetPassword.title" />
                </Title>
                <Subtitle>
                    <FormattedMessage id="resetPassword.subtitle" />
                </Subtitle>
                <InputWrapper>
                    <PasswordInput
                        value={password}
                        onChange={(value): unknown => setPassword(value)}
                        placeholder="••••••••"
                    />
                </InputWrapper>
                <InputWrapper>
                    <PasswordInput
                        value={confirm}
                        onChange={(value): unknown => setConfirm(value)}
                        placeholder="••••••••"
                    />
                </InputWrapper>

                {errorLabel ? <ErrorMessage>{errorLabel}</ErrorMessage> : null}

                <Actions>
                    <Button
                        buttonType="primary"
                        fullWidth
                        thick
                        disabled={
                            !password ||
                            !confirm ||
                            isLoading ||
                            tokenValidating ||
                            !isPasswordLongEnough ||
                            !passwordsMatch
                        }
                        onClick={handleSubmit}
                    >
                        <FormattedMessage id="resetPassword.submit" />
                    </Button>
                    <SecondaryLink type="button" onClick={() => history.push(Routes.Login)}>
                        <FormattedMessage id="resetPassword.backToLogin" />
                    </SecondaryLink>
                </Actions>
            </Card>
        </Page>
    );
};

export default ResetPassword;
